import { useState } from 'react';
import { Link } from 'react-router-dom';
import { contactAPI } from '@/lib/api';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { 
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { toast } from 'sonner';
import {
  HelpCircle,
  MessageCircle,
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  Search,
  BookOpen,
  CreditCard,
  FileText,
  Globe,
  GraduationCap,
  Users,
  Facebook,
  Loader2,
  Play
} from 'lucide-react';

const WHATSAPP_NUMBER = '923268096380';
const FACEBOOK_URL = 'https://www.facebook.com/share/1QFSsrjUfE/?mibextid=wwXIfr';

const HelpPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hello! I need help with my study abroad application.')}`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!contactForm.name || !contactForm.email || !contactForm.subject || !contactForm.message) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setSubmitting(true);
    try {
      await contactAPI.submit(contactForm);
      toast.success('Message sent! Our team will get back to you within 24 hours.');
      setContactForm({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const faqs = [
    {
      category: 'Applications',
      icon: FileText,
      questions: [
        {
          q: 'How do I apply to a university?',
          a: 'After creating your profile, browse courses and click "Apply Now" on any course card. Fill in the required details and submit your application. Our team will guide you through the next steps.'
        },
        {
          q: 'What documents do I need for my application?',
          a: 'Typically you need: Academic transcripts, English test scores (IELTS/PTE), Passport copy, Statement of Purpose, Letters of Recommendation, and CV/Resume. Requirements vary by university.'
        },
        {
          q: 'How long does the application process take?',
          a: 'Application processing typically takes 2-6 weeks depending on the university. Some universities offer faster decisions for complete applications.'
        }
      ]
    },
    {
      category: 'Finances',
      icon: CreditCard,
      questions: [
        {
          q: 'What payment methods are accepted?',
          a: 'We accept credit/debit cards, bank transfers, and offer flexible payment plans. Contact our finance team for specific payment arrangements.'
        },
        {
          q: 'Are there any scholarships available?',
          a: 'Yes! Many of our partner universities offer scholarships. Create your profile and our advisors will help identify scholarship opportunities you qualify for.'
        },
        {
          q: 'Can I pay in installments?',
          a: 'Yes, payment plans are available for tuition fees. Contact our finance team to discuss installment options based on your university and program.'
        }
      ]
    },
    {
      category: 'Visa & Travel',
      icon: Globe,
      questions: [
        {
          q: 'Do you help with visa applications?',
          a: 'Yes! Our team provides comprehensive visa guidance including document preparation, application review, and interview preparation for all study destinations.'
        },
        {
          q: 'When should I apply for my student visa?',
          a: 'We recommend starting your visa application as soon as you receive your offer letter. For UK, this is typically 3-6 months before your course starts.'
        },
        {
          q: 'What is a CAS number?',
          a: 'CAS (Confirmation of Acceptance for Studies) is required for UK student visa applications. Your university issues this after you accept your offer and pay the deposit.'
        }
      ]
    },
    {
      category: 'Agents',
      icon: Users,
      questions: [
        {
          q: 'How do I become an agent?',
          a: 'Click "Register as Agent" on our homepage, fill in your business details, and our team will review your application within 48 hours.'
        },
        {
          q: 'What commission do agents earn?',
          a: 'Agents earn competitive commissions on successful student placements. The more students you recruit, the higher your commission tier. Commission is paid after the student enrolls and begins their course.'
        },
        {
          q: 'How do I track my referrals?',
          a: 'Log into your agent dashboard to view all referrals, application statuses, and commission earnings in real-time.'
        }
      ]
    }
  ];

  const filteredFaqs = searchTerm
    ? faqs.map(cat => ({
        ...cat,
        questions: cat.questions.filter(q =>
          q.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
          q.a.toLowerCase().includes(searchTerm.toLowerCase())
        )
      })).filter(cat => cat.questions.length > 0)
    : faqs;

  const contactInfo = [
    { icon: Phone, label: 'Landline', value: '091-3094815', href: 'tel:0913094815' },
    { icon: Phone, label: 'For Students', value: '0332-9363599', href: 'tel:03329363599' },
    { icon: Phone, label: 'For Partnerships', value: '0326-8096380', href: 'tel:03268096380' },
    { icon: Mail, label: 'Partnerships', value: 'partnerships@hedirect.org', href: 'mailto:partnerships@hedirect.org' },
    { icon: Mail, label: 'General', value: 'info@hedirect.org', href: 'mailto:info@hedirect.org' },
    { icon: MessageCircle, label: 'WhatsApp', value: 'Chat with us', href: whatsappUrl, color: 'text-green-500' },
    { icon: Facebook, label: 'Facebook', value: 'Follow us', href: FACEBOOK_URL, color: 'text-blue-500' },
    { icon: Clock, label: 'Hours', value: 'Mon-Sat: 9AM - 6PM PKT', href: null }
  ];

  return (
    <div className="min-h-screen bg-[#f8f9fa] dark:bg-slate-900 transition-colors">
      <Navbar />
      
      <main className="pt-20 pb-12">
        {/* Hero */}
        <div className="bg-gradient-to-r from-teal-100/80 via-[#e9ecef] to-[#f1f3f5] py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-16 h-16 rounded-full bg-teal-600/10 flex items-center justify-center mx-auto mb-6">
              <HelpCircle className="w-8 h-8 text-teal-600" />
            </div>
            <h1 className="text-4xl font-semibold text-gray-800 mb-4">How Can We Help?</h1>
            <p className="text-gray-600 text-lg mb-8">
              Find answers to common questions or get in touch with our team
            </p>
            
            {/* Search */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 dark:text-gray-400" />
              <Input
                type="text"
                placeholder="Search for answers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-14 bg-white border-gray-200 focus:border-teal-500 text-lg"
                data-testid="help-search"
              />
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Quick Links - Bordered Section */}
          <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 mb-8 transition-colors">
            <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200 dark:border-slate-600">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Quick Links</h2>
                <p className="text-sm text-gray-500 dark:text-gray-400">Jump to common resources</p>
              </div>
            </div>
            <div className="grid md:grid-cols-5 gap-4">
              <Link to="/training" className="bg-gray-50 dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 rounded-xl p-5 hover:border-teal-400 transition-all group">
                <Play className="w-8 h-8 text-teal-600 dark:text-teal-400 mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="text-gray-800 dark:text-white font-medium mb-1">Training Center</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Interactive guides</p>
              </Link>
              <Link to="/check-eligibility" className="bg-gray-50 dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 rounded-xl p-5 hover:border-teal-400 transition-all group">
                <GraduationCap className="w-8 h-8 text-teal-600 dark:text-teal-400 mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="text-gray-800 dark:text-white font-medium mb-1">Check Eligibility</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">See if you qualify</p>
              </Link>
              <Link to="/courses" className="bg-gray-50 dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 rounded-xl p-5 hover:border-teal-400 transition-all group">
                <BookOpen className="w-8 h-8 text-teal-600 dark:text-teal-400 mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="text-gray-800 dark:text-white font-medium mb-1">Browse Courses</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Find your program</p>
              </Link>
              <Link to="/applications" className="bg-gray-50 dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 rounded-xl p-5 hover:border-teal-400 transition-all group">
                <FileText className="w-8 h-8 text-teal-600 dark:text-teal-400 mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="text-gray-800 dark:text-white font-medium mb-1">Track Applications</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Check your status</p>
              </Link>
              <Link to="/finances" className="bg-gray-50 dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 rounded-xl p-5 hover:border-teal-400 transition-all group">
                <CreditCard className="w-8 h-8 text-teal-600 dark:text-teal-400 mb-3 group-hover:scale-110 transition-transform" />
                <h3 className="text-gray-800 dark:text-white font-medium mb-1">Finances</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">Payments & fees</p>
              </Link>
            </div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* FAQs - Bordered Section */}
            <div className="lg:col-span-2">
              <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 transition-colors">
                <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200 dark:border-slate-600">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
                    <HelpCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Frequently Asked Questions</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Find answers to common questions</p>
                  </div>
                </div>
              
                {filteredFaqs.map((category) => (
                  <div key={category.category} className="mb-6">
                    <div className="flex items-center gap-2 mb-4">
                      <category.icon className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-white">{category.category}</h3>
                    </div>
                    
                    <Accordion type="single" collapsible className="space-y-2">
                      {category.questions.map((faq, idx) => (
                        <AccordionItem 
                          key={idx} 
                          value={`${category.category}-${idx}`}
                          className="bg-gray-50 dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 rounded-xl px-4"
                        >
                          <AccordionTrigger className="text-gray-800 dark:text-white hover:text-teal-600 dark:hover:text-teal-400 text-left py-4">
                            {faq.q}
                          </AccordionTrigger>
                          <AccordionContent className="text-gray-500 dark:text-gray-400 pb-4">
                            {faq.a}
                          </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>
              ))}

              {filteredFaqs.length === 0 && (
                <div className="bg-gray-50 dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 rounded-xl p-8 text-center">
                  <HelpCircle className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-2">No results found</h3>
                  <p className="text-gray-500 dark:text-gray-400">Try a different search term or contact our team</p>
                </div>
              )}
              </div>
            </div>

            {/* Contact Section - Bordered */}
            <div className="space-y-6">
              {/* Contact Info */}
              <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 transition-colors">
                <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200 dark:border-slate-600">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Contact Us</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Get in touch with our team</p>
                  </div>
                </div>
                <div className="space-y-4">
                  {contactInfo.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-700 rounded-xl border border-gray-200 dark:border-slate-600">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${item.color ? 'bg-gray-100 dark:bg-slate-600' : 'bg-teal-100 dark:bg-teal-900/50'}`}>
                        <item.icon className={`w-5 h-5 ${item.color || 'text-teal-600 dark:text-teal-400'}`} />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{item.label}</p>
                        {item.href ? (
                          <a href={item.href} target="_blank" rel="noopener noreferrer" className={`hover:opacity-80 transition-colors ${item.color || 'text-gray-800 dark:text-white hover:text-teal-600 dark:hover:text-teal-400'}`}>
                            {item.value}
                          </a>
                        ) : (
                          <p className="text-gray-800 dark:text-white">{item.value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Office Locations */}
              <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 transition-colors">
                <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200 dark:border-slate-600">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Our Offices</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Visit us in person</p>
                  </div>
                </div>
                <div className="space-y-4">
                  {/* Head Office - Peshawar */}
                  <div className="p-4 bg-gray-50 dark:bg-slate-700 rounded-xl border border-gray-200 dark:border-slate-600">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                      <p className="text-gray-800 dark:text-white font-medium">Head Office - Peshawar</p>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 ml-6">
                      Rafeys Height, 2nd Floor, Flat 306,<br />
                      Abdara Road, Near MM Academy,<br />
                      Main University Road, Peshawar, Pakistan
                    </p>
                  </div>
                  {/* Regional Office - Islamabad */}
                  <div className="p-4 bg-gray-50 dark:bg-slate-700 rounded-xl border border-gray-200 dark:border-slate-600">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="w-4 h-4 text-teal-600 dark:text-teal-400" />
                      <p className="text-gray-800 dark:text-white font-medium">Regional Office - Islamabad</p>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 ml-6">
                      Office No 1, 3rd Floor,<br />
                      Silver City Plaza, G11 Markaz,<br />
                      Sachal Sarmast Road, Islamabad, Pakistan
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white dark:bg-slate-800 border-2 border-gray-200 dark:border-slate-700 rounded-2xl p-6 transition-colors">
                <div className="flex items-center gap-3 mb-5 pb-4 border-b border-gray-200 dark:border-slate-600">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                    <Send className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Send a Message</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">We'll respond within 24 hours</p>
                  </div>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-gray-600 dark:text-gray-300">Name</Label>
                    <Input
                      value={contactForm.name}
                      onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                      className="bg-gray-50 dark:bg-slate-700 border-gray-200 dark:border-slate-600 dark:text-white"
                      required
                      data-testid="help-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-600 dark:text-gray-300">Email</Label>
                    <Input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                      className="bg-gray-50 dark:bg-slate-700 border-gray-200 dark:border-slate-600 dark:text-white"
                      required
                      data-testid="help-email"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-600 dark:text-gray-300">Subject</Label>
                    <Input
                      value={contactForm.subject}
                      onChange={(e) => setContactForm({...contactForm, subject: e.target.value})}
                      className="bg-white border-gray-200"
                      required
                      data-testid="help-subject"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-gray-600 dark:text-gray-300">Message</Label>
                    <Textarea
                      value={contactForm.message}
                      onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                      className="bg-white border-gray-200 min-h-[100px]"
                      required
                      data-testid="help-message"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-teal-600 text-white hover:bg-teal-700 font-semibold" 
                    data-testid="help-submit"
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default HelpPage;
